import axios from 'axios';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

const API_BASE_URL = 'https://api.frazza.online';

/**
 * Checks if user's subscription is still active with Stripe
 * If not active, revokes membership in Firebase for user and partner
 * Also checks partner's subscription if they share the same Customer ID
 */
export async function verifyAndUpdateSubscription(userId) {
    if (!userId) return;

    try {
        // Get user data from Firebase
        const userRef = doc(db, 'UserInformation', userId);
        const userSnap = await getDoc(userRef);

        if (!userSnap.exists()) {
            console.log('User not found');
            return;
        }

        const userData = userSnap.data();
        const partnerId = userData.partnerId || userData.inRelationWith;

        // If user is marked as member but has no stripeCustomerId, check if partner has one
        if (userData.member && !userData.stripeCustomerId && partnerId) {
            const partnerRef = doc(db, 'UserInformation', partnerId);
            const partnerSnap = await getDoc(partnerRef);
            
            if (partnerSnap.exists() && partnerSnap.data().stripeCustomerId) {
                // Partner has a customer ID, use it
                await updateDoc(userRef, {
                    stripeCustomerId: partnerSnap.data().stripeCustomerId
                });
                userData.stripeCustomerId = partnerSnap.data().stripeCustomerId;
            } else {
                // No customer ID anywhere but member is true - might be newly paid, don't revoke yet
                console.log('User marked as member but has no Stripe customer ID. Skipping check (might be newly paid).');
                return { pending: true };
            }
        }

        // If user doesn't have a customer ID and is not a member, nothing to check
        if (!userData.stripeCustomerId && !userData.member) {
            return;
        }

        // Check subscription status with Stripe via backend using userId (not customerId)
        const response = await axios.get(`${API_BASE_URL}/subscription-status/${userId}`);

        const status = response.data.status;
        const active = status === 'active' || status === 'trialing';

        // Update membership based on subscription status
        if (!active && userData.member) {
            // Subscription not active but user is marked as member - revoke access
            console.log(`Subscription no longer active (${status}). Revoking access...`);
            await revokeAccess(userRef, partnerId, status);
            return { revoked: true, status };
        } else if (active && !userData.member) {
            // Subscription active but user is not marked as member - grant access
            console.log(`Subscription is active (${status}). Granting access...`);
            await grantAccess(userRef, partnerId, userData.subscriptionPlan || 'monthly');
            return { granted: true, status };
        }

        return { active, status };
    } catch (error) {
        console.error('Error verifying subscription:', error);
        return { error: true };
    }
}

async function revokeAccess(userRef, partnerId, status) {
    // Revoke user's membership
    await updateDoc(userRef, {
        member: false,
        subscriptionPlan: null,
        subscriptionPending: false,
        subscriptionStatus: status,
        subscriptionCanceledAt: new Date()
    });

    // Revoke partner's membership
    if (partnerId) {
        const partnerRef = doc(db, 'UserInformation', partnerId);
        const partnerSnap = await getDoc(partnerRef);

        if (partnerSnap.exists()) {
            await updateDoc(partnerRef, {
                member: false,
                subscriptionPlan: null
            });
            console.log(`Revoked partner ${partnerId} membership as well.`);
        }
    }

    console.log('Membership revoked successfully');
}

async function grantAccess(userRef, partnerId, planType) {
    // Grant user's membership
    await updateDoc(userRef, {
        member: true,
        subscriptionPlan: planType,
        subscriptionPending: false,
        subscriptionCanceledAt: null,
        subscriptionStatus: 'active'
    });

    // Grant partner's membership
    if (partnerId) {
        const partnerRef = doc(db, 'UserInformation', partnerId);
        const partnerSnap = await getDoc(partnerRef);

        if (partnerSnap.exists()) {
            await updateDoc(partnerRef, {
                member: true,
                subscriptionPlan: planType,
                subscriptionCanceledAt: null,
                subscriptionStatus: 'active'
            });
            console.log(`Granted partner ${partnerId} membership as well.`);
        }
    }

    console.log('Membership granted successfully');
}
